import React, { useEffect, useState } from 'react';
import { Heading } from '../../../../../_lib/components/Heading/Heading';
import { murmurhash } from '../../../../../lib/js/hash';
import { useSonTranslation } from '../../../../../translation/useSonTranslation';
import { Row, Fieldset, RadioParam } from '../../../../../_lib/Form2';
import { CheckRadioForLists } from '../../../../../_lib/Form2';
import { CheckRadioForListsModifier } from '../../../../../_lib/Form2/elements/CheckRadioForLists/CheckRadioForLists';
import { RequiredSymbol } from '../../../../../_lib/Form2/partials/RequiredSymbol/RequiredSymbol';
import { Icons } from '../../../../../components/atoms/SVG';
import { HEX, TemplateIcon } from '../../../../../_lib/components/PageType/PageTypeIcon';
import { RowModifier } from '../../../../../_lib/Form2/grid/Row/Row';

export type TemplateChoiceProps = {
  params: RadioParam<'templatePageId'>[];
  selectedPageTitle: string;
  setSelectedFetchUrl: (url: string | undefined) => void;
};

/** sort params either by its orderKey property, or alphabetically if all have an orderKey 0 */
const sortParams = (params: (RadioParam<'templatePageId'> | undefined)[]) => {
  const sortByOrderKey = params.every(p => p?.orderKey !== undefined) && params.some(p => p?.orderKey !== 0);

  return sortByOrderKey // @ts-expect-error TS cannot know here that we already checked this for `sortByOrderKey`
    ? params.sort((a, b) => (a.orderKey > b.orderKey ? 1 : -1))
    : params.sort((a, b) => {
        if (a?.displayName && b?.displayName) {
          return a.displayName.localeCompare(b.displayName);
        }

        return 0;
      });
};

export function TemplateChoice({ params, selectedPageTitle, setSelectedFetchUrl }: TemplateChoiceProps) {
  const { t } = useSonTranslation('CreatePage');

  const [selectedTemplate, setSelectedTemplate] = useState<string | undefined>(
    params.find(param => param.checked)?.value
  );

  // need this useEffect to ensure preselection if select template tab is skipped on page creation
  useEffect(() => {
    if (!selectedTemplate) {
      setSelectedTemplate(params.find(param => param.checked)?.value);
    }
  }, [params, selectedTemplate]);

  // Cluster params by fieldset to be able to order navGroups and params within separately
  // as well allows to render navGroups
  const fieldSets: Map<string, RadioParam<'templatePageId'>[]> = params.reduce((acc, param) => {
    if (param.fieldSet) {
      const group = acc.get(param.fieldSet);

      if (group) {
        group.push(param);
      } else {
        acc.set(param.fieldSet, [param]);
      }
    }

    return acc;
  }, new Map<string, RadioParam<'templatePageId'>[]>());

  const checkboxNoTemplateParam = fieldSets.get('none')?.at(0);

  return (
    <>
      <Heading hierarchy={3} className="create-page-form__heading font-bold-16-20">
        <>
          {t('steps.templates.headline', { selectedPageTitle })}
          <RequiredSymbol />
        </>
      </Heading>
      <div className="create-page-form__selection">
        {checkboxNoTemplateParam ? (
          <Row modifier={RowModifier.BLOCK}>
            <CheckRadioForLists
              key={murmurhash(`none-${checkboxNoTemplateParam.value}`)}
              type="radio"
              modifier={CheckRadioForListsModifier.CREATE_PAGE}
              label={checkboxNoTemplateParam.displayName || t('steps.templates.defaultTemplate.label')}
              description={t('steps.templates.defaultTemplate.description')}
              icon={
                <TemplateIcon
                  icon={Icons.BLANK_PAGE}
                  borderColor={checkboxNoTemplateParam.templateCategories?.at(0)?.hexColor as HEX}
                />
              }
              param={checkboxNoTemplateParam}
              checked={checkboxNoTemplateParam.value === selectedTemplate}
              onChange={event => {
                setSelectedTemplate(event.target.value);
                setSelectedFetchUrl(checkboxNoTemplateParam.formParamsFetchUrl);
              }}
            />
          </Row>
        ) : null}
        {fieldSets
          .entries()
          .toArray()
          .filter(([key]) => key !== 'none')
          .map(([key, value]) => (
            <Row key={key} modifier={RowModifier.BLOCK}>
              {/* @ts-expect-error -- known issue from translation typing */}
              <Fieldset legend={t(`steps.templates.fieldSet.${key}.legend`)}>
                {sortParams(value).map(param => (
                  <CheckRadioForLists
                    key={murmurhash(`${key}-${param?.value}`)}
                    type="radio"
                    modifier={CheckRadioForListsModifier.CREATE_PAGE}
                    label={param?.displayName || t('steps.templates.defaultTemplate.label')}
                    description={
                      <>
                        {param?.description ? <span>{param?.description}</span> : null}
                        {param?.templateCategories && param?.templateCategories.length > 0 ? (
                          <span>
                            <dfn className="create-page-form__categories-label font-bold-14-19">
                              {t('categories')}:{' '}
                            </dfn>
                            {param?.templateCategories.map(e => e.title).join(', ')}
                          </span>
                        ) : null}
                      </>
                    }
                    icon={
                      <TemplateIcon
                        icon={param?.value ? Icons.TEMPLATE : Icons.BLANK_PAGE}
                        borderColor={param?.templateCategories?.at(0)?.hexColor as HEX}
                      />
                    }
                    param={param}
                    checked={param?.value === selectedTemplate}
                    onChange={event => {
                      setSelectedTemplate(event.target.value);
                      setSelectedFetchUrl(param?.formParamsFetchUrl);
                    }}
                  />
                ))}
              </Fieldset>
            </Row>
          ))}
      </div>
    </>
  );
}
